import { supabase } from '@/lib/supabase';
import type { Profile } from '@/types';
import type { ReactionRow, ViewMember, ViewMessage } from '@/features/communities/api/communityView';

type MiniProfile = Pick<Profile, 'id' | 'display_name' | 'avatar_url' | 'handle' | 'role' | 'email'>;

const MEDIA_BUCKET = 'message-media';
const SIGNED_URL_TTL = 60 * 60;
const UUID_RE = /^[0-9a-f-]{36}$/i;

async function fetchProfiles(ids: string[]): Promise<Map<string, MiniProfile>> {
  const map = new Map<string, MiniProfile>();
  if (!ids.length) return map;
  const { data, error } = await supabase
    .from('profiles')
    .select('id, display_name, avatar_url, handle, role, email')
    .in('id', ids);
  if (error) throw error;
  for (const p of data ?? []) map.set(p.id, p);
  return map;
}

function isExternalMedia(media: string): boolean {
  return /^https?:\/\//i.test(media);
}

async function resolveMediaUrls(medias: string[]): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  const paths = medias.filter((m) => !isExternalMedia(m));
  for (const m of medias) if (isExternalMedia(m)) out.set(m, m);
  if (paths.length) {
    const { data } = await supabase.storage
      .from(MEDIA_BUCKET)
      .createSignedUrls([...new Set(paths)], SIGNED_URL_TTL);
    for (const item of data ?? []) {
      if (item.path && item.signedUrl) out.set(item.path, item.signedUrl);
    }
  }
  return out;
}

export async function getOrCreateDm(otherUserId: string): Promise<string> {
  const { data, error } = await supabase.rpc('get_or_create_dm', { other: otherUserId });
  if (error) throw error;
  return data;
}

export async function getConversationMembers(conversationId: string): Promise<ViewMember[]> {
  const { data, error } = await supabase
    .from('conversation_members')
    .select('user_id, role')
    .eq('conversation_id', conversationId);
  if (error) throw error;
  const rows = data ?? [];
  const profiles = await fetchProfiles(rows.map((r) => r.user_id));
  return rows.map((r) => ({
    user_id: r.user_id,
    role: 'MEMBER',
    online: false,
    member_number: null,
    founding: false,
    profile: profiles.get(r.user_id) ?? null,
  }));
}

export async function getConversationPeer(conversationId: string, currentUserId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('conversation_members')
    .select('user_id')
    .eq('conversation_id', conversationId);
  if (error) throw error;
  const peerId = (data ?? []).map((r) => r.user_id).find((id) => id !== currentUserId);
  if (!peerId) return null;
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', peerId)
    .single();
  if (profileError) throw profileError;
  return profile;
}

export async function listMessages(conversationId: string): Promise<ViewMessage[]> {
  const { data, error } = await supabase
    .from('messages')
    .select('id, sender_id, body, reply_to_id, created_at, edited_at')
    .eq('conversation_id', conversationId)
    .is('deleted_at', null)
    .order('created_at', { ascending: true })
    .limit(400);
  if (error) throw error;

  const rows = data ?? [];
  const ids = rows.map((r) => r.id);
  const [{ data: attachments, error: attachmentError }, authors] = await Promise.all([
    ids.length
      ? supabase
          .from('message_attachments')
          .select('message_id, storage_path')
          .in('message_id', ids)
          .eq('kind', 'image')
      : Promise.resolve({ data: [], error: null }),
    fetchProfiles([...new Set(rows.map((r) => r.sender_id))]),
  ]);
  if (attachmentError) throw attachmentError;

  const mediaByMessage = new Map((attachments ?? []).map((a) => [a.message_id, a.storage_path] as const));
  const mediaUrls = await resolveMediaUrls([...mediaByMessage.values()]);

  return rows.map((r) => {
    const media = mediaByMessage.get(r.id) ?? null;
    return {
      id: r.id,
      author_id: r.sender_id,
      body: r.body,
      media,
      mediaUrl: media ? (mediaUrls.get(media) ?? null) : null,
      channel_id: 'dm',
      reply_to_id: r.reply_to_id,
      created_at: r.created_at,
      edited_at: r.edited_at,
      author: authors.get(r.sender_id) ?? null,
    };
  });
}

export async function sendMessage(input: {
  conversationId: string;
  senderId: string;
  body: string;
  media?: string | null;
  replyToId?: string | null;
}): Promise<void> {
  const { data, error } = await supabase
    .from('messages')
    .insert({
      conversation_id: input.conversationId,
      sender_id: input.senderId,
      body: input.body || null,
      reply_to_id: input.replyToId ?? null,
    })
    .select('id')
    .single();
  if (error) throw error;

  if (input.media) {
    const isExternal = isExternalMedia(input.media);
    const { error: attachmentError } = await supabase.from('message_attachments').insert({
      message_id: data.id,
      kind: 'image',
      storage_path: input.media,
      mime_type: isExternal ? 'image/gif' : 'image/png',
    });
    if (attachmentError) throw attachmentError;
  }
}

export async function uploadMessageImage(file: File, conversationId: string): Promise<string> {
  const ext = (file.name.split('.').pop() || 'png').toLowerCase().replace(/[^a-z0-9]/g, '');
  const path = `dm/${conversationId}/${crypto.randomUUID()}.${ext || 'png'}`;
  const { error } = await supabase.storage
    .from(MEDIA_BUCKET)
    .upload(path, file, { contentType: file.type || 'image/png', upsert: false });
  if (error) throw error;
  return path;
}

export async function editMessage(id: string, body: string): Promise<void> {
  if (!UUID_RE.test(id)) return;
  const { error } = await supabase
    .from('messages')
    .update({ body, edited_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function deleteMessage(id: string): Promise<void> {
  if (!UUID_RE.test(id)) return;
  const { error } = await supabase
    .from('messages')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export interface MessageSearchHit {
  messageId: string;
  conversationId: string;
  body: string;
  createdAt: string;
  author: MiniProfile | null;
  peer: MiniProfile | null;
}

/**
 * Search the bodies of messages in the user's conversations (Discord-style
 * inbox search). RLS already scopes `messages` to conversations the viewer
 * belongs to, so a plain ilike returns only their own chat history. Results are
 * enriched with the sender and the DM peer (which chat it came from).
 */
export async function searchMyMessages(userId: string, query: string): Promise<MessageSearchHit[]> {
  const q = query.trim().replace(/[%_]/g, (m) => `\\${m}`);
  if (q.length < 2) return [];
  const { data, error } = await supabase
    .from('messages')
    .select('id, body, sender_id, conversation_id, created_at')
    .ilike('body', `%${q}%`)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    .limit(25);
  if (error) throw error;
  const rows = data ?? [];
  if (!rows.length) return [];

  const conversationIds = [...new Set(rows.map((r) => r.conversation_id))];
  const { data: memberRows } = await supabase
    .from('conversation_members')
    .select('conversation_id, user_id')
    .in('conversation_id', conversationIds);
  const peerByConversation = new Map<string, string>();
  for (const row of memberRows ?? []) {
    if (row.user_id !== userId) peerByConversation.set(row.conversation_id, row.user_id);
  }

  const profiles = await fetchProfiles([
    ...new Set([...rows.map((r) => r.sender_id), ...peerByConversation.values()]),
  ]);

  return rows.map((r) => {
    const peerId = peerByConversation.get(r.conversation_id) ?? null;
    return {
      messageId: r.id,
      conversationId: r.conversation_id,
      body: r.body ?? '',
      createdAt: r.created_at,
      author: profiles.get(r.sender_id) ?? null,
      peer: peerId ? (profiles.get(peerId) ?? null) : null,
    };
  });
}

export async function listReactions(messageIds: string[]): Promise<ReactionRow[]> {
  if (!messageIds.length) return [];
  const { data, error } = await supabase
    .from('message_reactions')
    .select('message_id, user_id, emoji')
    .in('message_id', messageIds);
  if (error) throw error;
  return data ?? [];
}

export async function toggleReaction(messageId: string, userId: string, emoji: string): Promise<void> {
  if (!UUID_RE.test(messageId)) return;
  const { data: existing } = await supabase
    .from('message_reactions')
    .select('message_id')
    .eq('message_id', messageId)
    .eq('user_id', userId)
    .eq('emoji', emoji)
    .maybeSingle();

  if (existing) {
    const { error } = await supabase
      .from('message_reactions')
      .delete()
      .eq('message_id', messageId)
      .eq('user_id', userId)
      .eq('emoji', emoji);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('message_reactions').insert({ message_id: messageId, user_id: userId, emoji });
    if (error) throw error;
  }
}
